import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { trackToolAction } from '../utils/metrics';
import AreaSearchMap from '../components/AreaSearchMap';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { db } from '../firebase';
import {
  doc, setDoc, updateDoc, deleteDoc, addDoc, collection,
  onSnapshot, query, orderBy, serverTimestamp, writeBatch,
} from 'firebase/firestore';
import { useLocationTracking } from '../utils/useLocationTracking';
import { loadOrCreateParticipant, saveParticipantName, getLastDisplayName } from '../utils/participant';
import { reverseGeocode } from '../utils/geocode';
import { computeGridCells, cellDocId, computeCellTier, suggestCellSize } from '../utils/gridSearch';

const GRID_CELLS_PER_BATCH = 450; // stays under Firestore's 500-op batch limit

// 22-char capability id (~131 bits) — same scheme as Video Canvass's makeId():
// the whole access model rests on this being unguessable.
function makeId(len = 22) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const arr = new Uint32Array(len);
  crypto.getRandomValues(arr);
  let s = '';
  for (let i = 0; i < len; i++) s += chars[arr[i] % chars.length];
  return s;
}

const DEFAULT_CENTER = { lat: 41.03, lng: -73.76, zoom: 13 };
const STALE_MS = 3 * 60 * 1000; // no fresh fix in 3min while "sharing" reads as stale, not active

// ─── Export ─────────────────────────────────────────────────────────────────
// Runs 100% client-side (no upload), consistent with the app's privacy
// guarantee: fetches the same public OSM tiles the live map uses and paints
// the boundary, every participant's trail, and every pin on top.

const safeFileName = s =>
  (s || 'area-search').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'area-search';

const TILE = 256;
const lngToWorldX = (lng, z) => ((lng + 180) / 360) * TILE * 2 ** z;
const latToWorldY = (lat, z) => {
  const s = Math.sin((lat * Math.PI) / 180);
  return (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * TILE * 2 ** z;
};

function loadTileImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

// Degree-box approximation of a boundary's extent, for framing only (not
// rendering) — same reasoning as AreaSearchMap's fit-to-boundary effect.
function boundaryExtentLatLngs(boundary) {
  if (!boundary) return [];
  if (boundary.type === 'circle' && boundary.center && boundary.radiusMeters) {
    const { lat, lng } = boundary.center;
    const latDelta = boundary.radiusMeters / 111320;
    const lngDelta = boundary.radiusMeters / (111320 * Math.cos((lat * Math.PI) / 180) || 1);
    return [{ lat: lat - latDelta, lng: lng - lngDelta }, { lat: lat + latDelta, lng: lng + lngDelta }];
  }
  if (boundary.type === 'polygon' && Array.isArray(boundary.points)) return boundary.points;
  return [];
}

// Static map image framed to the boundary + every trail point + every pin, or
// null (nothing to show, or tiles blocked) — the PDF then just omits the map.
// Grid search mode canvas colors — flat hex duplicates of AreaSearchMap.js's
// TIER_STYLE, matching this file's existing precedent of duplicating small
// color constants across the live map and the static PDF renderer (e.g. the
// boundary's cyan is already duplicated the same way).
const TIER_FILL = { none: '#94a3b8', possible: '#f59e0b', confirmed: '#16a34a' };
const TIER_FILL_ALPHA = { none: 0.08, possible: 0.35, confirmed: 0.45 };
const TIER_STROKE = { none: '#94a3b8', possible: '#d97706', confirmed: '#16a34a' };
const GRID_LABEL_MIN_PX = 18; // only draw a cell's code if it'll be legible at this on-screen size

async function renderAreaSearchMapImage(boundary, trails, pins, gridCells = [], width = 1000, height = 560) {
  const pts = [
    ...boundaryExtentLatLngs(boundary),
    ...trails.flatMap(t => t.path || []),
    ...pins.filter(p => p.lat != null && p.lng != null),
  ].filter(p => p.lat != null && p.lng != null);
  if (!pts.length) return null;

  const lats = pts.map(p => +p.lat), lngs = pts.map(p => +p.lng);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);

  const pad = 1.15;
  let zoom = 1;
  for (let z = 18; z >= 1; z--) {
    const w = (lngToWorldX(maxLng, z) - lngToWorldX(minLng, z)) * pad;
    const h = (latToWorldY(minLat, z) - latToWorldY(maxLat, z)) * pad;
    if (w <= width && h <= height) { zoom = z; break; }
  }
  if (minLat === maxLat && minLng === maxLng) zoom = 16;

  const centerX = (lngToWorldX(minLng, zoom) + lngToWorldX(maxLng, zoom)) / 2;
  const centerY = (latToWorldY(minLat, zoom) + latToWorldY(maxLat, zoom)) / 2;
  const originX = centerX - width / 2;
  const originY = centerY - height / 2;
  const project = (lat, lng) => ({ x: lngToWorldX(lng, zoom) - originX, y: latToWorldY(lat, zoom) - originY });

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#e5e7eb';
  ctx.fillRect(0, 0, width, height);

  const n = 2 ** zoom;
  const subs = ['a', 'b', 'c'];
  const x0 = Math.floor(originX / TILE), x1 = Math.floor((originX + width) / TILE);
  const y0 = Math.floor(originY / TILE), y1 = Math.floor((originY + height) / TILE);
  const jobs = [];
  for (let tx = x0; tx <= x1; tx++) {
    for (let ty = y0; ty <= y1; ty++) {
      if (ty < 0 || ty >= n) continue;
      const wx = ((tx % n) + n) % n;
      const s = subs[Math.abs(tx + ty) % subs.length];
      const url = `https://${s}.tile.openstreetmap.org/${zoom}/${wx}/${ty}.png`;
      const dx = tx * TILE - originX, dy = ty * TILE - originY;
      jobs.push(loadTileImage(url).then(img => ctx.drawImage(img, dx, dy)).catch(() => {}));
    }
  }
  await Promise.all(jobs);

  // Grid search mode: colored cell rectangles, drawn before the boundary
  // outline so trails/pins/boundary still render on top — matches the live
  // map's layering. Code labels only drawn if the cell is large enough
  // on-screen to be legible; otherwise the cell is color-only.
  for (const cell of gridCells) {
    const nw = project(cell.boundsNorth, cell.boundsWest);
    const se = project(cell.boundsSouth, cell.boundsEast);
    const x = Math.min(nw.x, se.x), y = Math.min(nw.y, se.y);
    const w = Math.abs(se.x - nw.x), h = Math.abs(se.y - nw.y);
    const tier = cell.tier || 'none';
    ctx.save();
    ctx.globalAlpha = TIER_FILL_ALPHA[tier];
    ctx.fillStyle = TIER_FILL[tier];
    ctx.fillRect(x, y, w, h);
    ctx.globalAlpha = 1;
    ctx.strokeStyle = TIER_STROKE[tier];
    ctx.lineWidth = 1;
    ctx.strokeRect(x, y, w, h);
    ctx.restore();
    if (cell.code && Math.min(w, h) >= GRID_LABEL_MIN_PX) {
      ctx.save();
      ctx.fillStyle = '#334155';
      ctx.font = '9px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(cell.code, x + w / 2, y + h / 2);
      ctx.restore();
    }
  }

  // Boundary outline.
  if (boundary) {
    ctx.save();
    ctx.strokeStyle = '#0891b2';
    ctx.setLineDash([6, 5]);
    ctx.lineWidth = 2;
    if (boundary.type === 'circle' && boundary.center && boundary.radiusMeters) {
      const c = project(boundary.center.lat, boundary.center.lng);
      const latDelta = boundary.radiusMeters / 111320;
      const edge = project(boundary.center.lat + latDelta, boundary.center.lng);
      const r = Math.abs(edge.y - c.y);
      ctx.beginPath();
      ctx.arc(c.x, c.y, r, 0, 2 * Math.PI);
      ctx.stroke();
    } else if (boundary.type === 'polygon' && Array.isArray(boundary.points) && boundary.points.length >= 3) {
      ctx.beginPath();
      boundary.points.forEach((p, i) => {
        const pt = project(p.lat, p.lng);
        if (i === 0) ctx.moveTo(pt.x, pt.y); else ctx.lineTo(pt.x, pt.y);
      });
      ctx.closePath();
      ctx.stroke();
    }
    ctx.restore();
  }

  // Per-participant trails.
  for (const t of trails) {
    if (!t.path || t.path.length < 2) continue;
    ctx.beginPath();
    t.path.forEach((p, i) => {
      const pt = project(+p.lat, +p.lng);
      if (i === 0) ctx.moveTo(pt.x, pt.y); else ctx.lineTo(pt.x, pt.y);
    });
    ctx.strokeStyle = t.color || '#334155';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
  }

  // Pins: colored dot (mirrors AreaSearchMap's pin styling, no direction arrow).
  for (const p of pins) {
    if (p.lat == null || p.lng == null) continue;
    const pt = project(+p.lat, +p.lng);
    const color = p.color || '#0f172a';
    ctx.beginPath();
    ctx.arc(pt.x, pt.y, 7, 0, 2 * Math.PI);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = '#ffffff';
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(pt.x, pt.y, 8.5, 0, 2 * Math.PI);
    ctx.lineWidth = 1;
    ctx.strokeStyle = color;
    ctx.stroke();
  }

  try {
    return canvas.toDataURL('image/png');
  } catch {
    return null; // canvas tainted (tiles blocked CORS) — omit the map gracefully
  }
}

async function exportAreaSearchPdf(meta, participants, trails, pins, gridCells = []) {
  const { default: pdfMake } = await import('pdfmake/build/pdfmake');
  const pdfFonts = await import('pdfmake/build/vfs_fonts');
  pdfMake.vfs = pdfFonts.default || pdfFonts;

  const mapImage = await renderAreaSearchMapImage(meta.boundary, trails, pins, gridCells).catch(() => null);

  const boundaryDesc = !meta.boundary ? 'not set'
    : meta.boundary.type === 'circle' ? `radius circle, ${Math.round(meta.boundary.radiusMeters || 0)}m`
    : `freeform zone, ${(meta.boundary.points || []).length} points`;

  const fmtWhen = ts => {
    const d = toDate(ts);
    return d ? d.toLocaleString() : '';
  };

  const participantsSection = participants.length === 0
    ? [{ text: 'No one has joined yet.', italics: true, color: '#94a3b8', margin: [0, 0, 0, 14] }]
    : [{
        table: {
          widths: [14, 140, '*'],
          body: participants.map(p => ([
            { canvas: [{ type: 'rect', x: 1, y: 2, w: 10, h: 10, r: 2, color: p.color || '#0f172a' }] },
            { text: p.displayName || 'Unknown', margin: [0, 1, 0, 1] },
            { text: p.sharing ? 'Sharing now' : `Last seen ${fmtWhen(p.lastSeenAt) || 'a while ago'}`, fontSize: 9, color: '#64748b' },
          ])),
        },
        layout: 'noBorders',
        margin: [0, 0, 0, 14],
      }];

  const pinsSection = pins.length === 0
    ? [{ text: 'No pins recorded.', italics: true, color: '#94a3b8' }]
    : [{
        table: {
          headerRows: 1,
          widths: [16, '*', 80, 95, '*'],
          body: [
            [
              { text: '#', bold: true, color: '#ffffff', fillColor: '#0f172a' },
              { text: 'Address', bold: true, color: '#ffffff', fillColor: '#0f172a' },
              { text: 'Dropped by', bold: true, color: '#ffffff', fillColor: '#0f172a' },
              { text: 'When', bold: true, color: '#ffffff', fillColor: '#0f172a' },
              { text: 'Note', bold: true, color: '#ffffff', fillColor: '#0f172a' },
            ],
            ...pins.map((p, i) => ([
              { text: String(i + 1) },
              { text: p.address || `${(+p.lat).toFixed(5)}, ${(+p.lng).toFixed(5)}` },
              { text: p.authorName || '' },
              { text: fmtWhen(p.createdAt) },
              { text: p.note || '' },
            ])),
          ],
        },
        layout: {
          hLineWidth: () => 0.5, vLineWidth: () => 0.5,
          hLineColor: () => '#e2e8f0', vLineColor: () => '#e2e8f0',
        },
      }];

  // Grid search mode: the map image already carries the colored cells (and
  // codes, where legible) — no separate per-cell table. A confirmed-cell
  // count folds into the summary line instead, mirroring the board's compact
  // header summary rather than a long list.
  const gridEnabled = Boolean(meta.grid && meta.grid.enabled);
  const gridConfirmedCount = gridEnabled ? gridCells.filter(c => c.tier === 'confirmed').length : 0;

  const docDefinition = {
    pageOrientation: 'landscape',
    pageMargins: [28, 36, 28, 36],
    defaultStyle: { fontSize: 9 },
    content: [
      { text: meta.title || 'Area Search', fontSize: 16, bold: true, margin: [0, 0, 0, 2] },
      meta.vehicleDesc ? { text: meta.vehicleDesc, color: '#475569', margin: [0, 0, 0, 2] } : null,
      { text: `Boundary: ${boundaryDesc}${gridEnabled ? ` · Grid: ${gridConfirmedCount}/${meta.grid.cellCount} cells confirmed (${meta.grid.cellSizeMeters}m)` : ''} · ${participants.length} participant${participants.length !== 1 ? 's' : ''} · ${pins.length} pin${pins.length !== 1 ? 's' : ''} · Exported ${new Date().toLocaleString()}`,
        color: '#64748b', fontSize: 8, margin: [0, 0, 0, 10] },
      mapImage
        ? { image: mapImage, width: 785, margin: [0, 0, 0, 14] }
        : { text: 'Map unavailable.', italics: true, color: '#94a3b8', margin: [0, 0, 0, 14] },
      { text: 'Participants', fontSize: 12, bold: true, color: '#0f172a', margin: [0, 0, 0, 6] },
      ...participantsSection,
      { text: 'Pins', fontSize: 12, bold: true, color: '#0f172a', margin: [0, 0, 0, 6] },
      ...pinsSection,
    ].filter(Boolean),
  };

  pdfMake.createPdf(docDefinition).download(`${safeFileName(meta.title)}.pdf`);
}

function toDate(v) {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  if (v instanceof Date) return isNaN(v) ? null : v;
  if (typeof v.seconds === 'number') return new Date(v.seconds * 1000);
  return null;
}

function fmtAgo(v) {
  const d = toDate(v);
  if (!d) return '';
  const s = Math.max(0, Math.round((Date.now() - d.getTime()) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  return `${Math.round(s / 3600)}h ago`;
}

function metersLabel(m) {
  if (!m) return '0 m';
  const ft = m * 3.28084;
  return m >= 1000 ? `${(m / 1000).toFixed(2)} km (${(ft / 5280).toFixed(2)} mi)` : `${Math.round(m)} m (${Math.round(ft)} ft)`;
}

// ─── Consent banner ─────────────────────────────────────────────────────────

function SharedLocationBanner() {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', gap: 1.25,
      bgcolor: '#ecfeff', border: '1px solid #a5f3fc', color: '#155e75',
      borderRadius: '10px', p: '12px 16px', mb: 2.5, fontSize: '13px', lineHeight: 1.5,
    }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        style={{ flexShrink: 0, marginTop: '1px' }}>
        <circle cx="12" cy="12" r="3"/><path d="M12 2v4M12 18v4M2 12h4M18 12h4"/>
      </svg>
      <span>
        <strong>Cloud-backed, real-time location sharing.</strong> When you tap Start Sharing, your live GPS
        position is sent to this app's server continuously and shown, in real time, to anyone who has this
        search's link. Your driven trail and pins stay visible to link-holders even after you stop sharing —
        Stop only halts new updates, it doesn't hide what's already recorded. Only share while actively
        participating, and keep the link inside your team.
      </span>
    </Box>
  );
}

// ─── Create / landing view ──────────────────────────────────────────────────

function CreateAreaSearch() {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [vehicleDesc, setVehicleDesc] = useState('');
  const [busy, setBusy] = useState(false);
  const [joinVal, setJoinVal] = useState('');
  const [mapCenter, setMapCenter] = useState(null);
  const [drawShape, setDrawShape] = useState('circle');
  const [drawing, setDrawing] = useState(true);
  const [boundaryDraft, setBoundaryDraft] = useState(null);
  const [confirmedBoundary, setConfirmedBoundary] = useState(null);
  const [resetToken, setResetToken] = useState(0);
  const [gridEnabled, setGridEnabled] = useState(false);
  const [cellSizeMeters, setCellSizeMeters] = useState(5);
  const [gridPreview, setGridPreview] = useState(null); // {cells, rows, cols} | null
  const [gridError, setGridError] = useState('');
  const [gridCreating, setGridCreating] = useState('');

  // Re-suggest a starting cell size (5m, scaled up only as far as needed to
  // stay under the cell cap) whenever grid mode turns on or the boundary is
  // (re)confirmed while it's on — not on every keystroke in the cell-size
  // field, so a manual edit afterward is never fought/overwritten.
  useEffect(() => {
    if (!gridEnabled || !confirmedBoundary) return;
    setCellSizeMeters(suggestCellSize(confirmedBoundary));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridEnabled, confirmedBoundary]);

  // Debounced live grid preview, recomputed whenever the boundary, grid
  // toggle, or cell size changes.
  useEffect(() => {
    if (!gridEnabled || !confirmedBoundary) { setGridPreview(null); setGridError(''); return; }
    const t = setTimeout(() => {
      const result = computeGridCells(confirmedBoundary, cellSizeMeters);
      if (result.ok) { setGridPreview(result); setGridError(''); }
      else { setGridPreview(null); setGridError(`This would create ${result.count} cells — the limit is 2500. Increase cell size or shrink the boundary.`); }
    }, 300);
    return () => clearTimeout(t);
  }, [gridEnabled, confirmedBoundary, cellSizeMeters]);

  const gridPreviewCells = useMemo(() => (
    gridEnabled && gridPreview
      ? gridPreview.cells.map(c => ({ ...c, id: cellDocId(c.row, c.col), tier: 'none' }))
      : []
  ), [gridEnabled, gridPreview]);

  useEffect(() => {
    if (!navigator.geolocation) return;
    const timer = setTimeout(() => {}, 4000);
    navigator.geolocation.getCurrentPosition(
      pos => { clearTimeout(timer); setMapCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude, zoom: 15 }); },
      () => clearTimeout(timer),
      { timeout: 4000 },
    );
    return () => clearTimeout(timer);
  }, []);

  function startOver() {
    setBoundaryDraft(null);
    setConfirmedBoundary(null);
    setResetToken(t => t + 1);
    setDrawing(true);
  }

  function confirmBoundary() {
    if (!boundaryDraft || !boundaryDraft.closed) return;
    setConfirmedBoundary(boundaryDraft);
    setDrawing(false);
  }

  async function create() {
    if (busy || !confirmedBoundary) return;
    if (gridEnabled && (!gridPreview || gridError)) return;
    setBusy(true);
    const id = makeId();
    try {
      const grid = gridEnabled && gridPreview
        ? { enabled: true, cellSizeMeters, rows: gridPreview.rows, cols: gridPreview.cols, cellCount: gridPreview.cells.length }
        : null;
      await setDoc(doc(db, 'areaSearches', id), {
        title: title.trim() || 'Untitled search',
        vehicleDesc: vehicleDesc.trim(),
        boundary: confirmedBoundary.type === 'circle'
          ? { type: 'circle', center: confirmedBoundary.center, radiusMeters: confirmedBoundary.radiusMeters }
          : { type: 'polygon', points: confirmedBoundary.points },
        ...(grid ? { grid } : {}),
        center: mapCenter || DEFAULT_CENTER,
        createdAt: serverTimestamp(),
        lastActivityAt: serverTimestamp(),
      });

      if (grid) {
        try {
          setGridCreating('Creating grid…');
          const cellsCol = collection(db, 'areaSearches', id, 'gridCells');
          const cells = gridPreview.cells;
          for (let i = 0; i < cells.length; i += GRID_CELLS_PER_BATCH) {
            const chunk = cells.slice(i, i + GRID_CELLS_PER_BATCH);
            setGridCreating(`Creating grid… ${Math.min(i + chunk.length, cells.length)}/${cells.length} cells`);
            const batch = writeBatch(db);
            for (const cell of chunk) {
              batch.set(doc(cellsCol, cellDocId(cell.row, cell.col)), {
                row: cell.row, col: cell.col, code: cell.code,
                centerLat: cell.centerLat, centerLng: cell.centerLng,
                boundsSouth: cell.boundsSouth, boundsWest: cell.boundsWest,
                boundsNorth: cell.boundsNorth, boundsEast: cell.boundsEast,
                autoHitTrailPointIds: {}, manualOverride: false,
                createdAt: serverTimestamp(),
              });
            }
            await batch.commit();
          }
        } catch (e) {
          alert(`Grid setup failed partway — the search itself was created and still works; you can start a new search to try again. (${e.message})`);
        } finally {
          setGridCreating('');
        }
      }

      navigate(`/areasearch/${id}`);
    } catch (e) {
      setBusy(false);
      setGridCreating('');
      alert(`Could not create the search: ${e.message}`);
    }
  }

  function join() {
    const v = joinVal.trim();
    if (!v) return;
    const m = v.match(/areasearch\/([A-Za-z0-9]+)/);
    navigate(`/areasearch/${m ? m[1] : v}`);
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.4px' }}>
          Area Search Coordinator
        </Typography>
        <Typography sx={{ color: '#64748b', mt: '6px', fontSize: '15px' }}>
          Start a shared map for a team sweeping a neighborhood: mark the target zone, send the link,
          and everyone's driven route draws live so you can see what's been covered.
        </Typography>
      </Box>

      <SharedLocationBanner />

      <Paper elevation={0} sx={{
        borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)', p: '24px',
      }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', mb: 2 }}>Join an existing search</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth size="small" label="Paste team link or ID"
            value={joinVal} onChange={e => setJoinVal(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') join(); }}
          />
          <Button variant="outlined" onClick={join}
            sx={{ borderColor: '#0891b2', color: '#0891b2', fontWeight: 600, whiteSpace: 'nowrap' }}>
            Open
          </Button>
        </Box>
      </Paper>
      <br/>

      <Paper elevation={0} sx={{
        borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)',
        p: '24px', mb: 2.5,
      }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', mb: 2 }}>Start a new search</Typography>
        <TextField
          fullWidth size="small" label="Search name" placeholder="e.g. Elm St — silver Honda Civic"
          value={title} onChange={e => setTitle(e.target.value)} sx={{ mb: 2 }}
        />
        <TextField
          fullWidth size="small" label="Vehicle / subject description (optional)"
          placeholder="e.g. silver Honda Civic, plate partial ABC, last seen NB on Elm"
          value={vehicleDesc} onChange={e => setVehicleDesc(e.target.value)} sx={{ mb: 2.5 }}
        />

        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', mb: 1 }}>Search boundary</Typography>

        {!confirmedBoundary && (
          <>
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
              <Button size="small" variant={drawShape === 'circle' ? 'contained' : 'outlined'}
                onClick={() => { setDrawShape('circle'); setBoundaryDraft(null); }}
                sx={drawShape === 'circle'
                  ? { bgcolor: '#0891b2', '&:hover': { bgcolor: '#0e7490' } }
                  : { borderColor: '#0891b2', color: '#0891b2' }}>
                Radius circle
              </Button>
              <Button size="small" variant={drawShape === 'polygon' ? 'contained' : 'outlined'}
                onClick={() => { setDrawShape('polygon'); setBoundaryDraft(null); }}
                sx={drawShape === 'polygon'
                  ? { bgcolor: '#0891b2', '&:hover': { bgcolor: '#0e7490' } }
                  : { borderColor: '#0891b2', color: '#0891b2' }}>
                Freeform shape
              </Button>
            </Box>
            <Typography sx={{ fontSize: '12px', color: '#64748b', mb: 1 }}>
              {drawShape === 'circle'
                ? 'Click the map to set the center, then click again to set the radius.'
                : 'Click to add corners; click the first point again (or double-click) to close the shape — at least 3 points.'}
            </Typography>
          </>
        )}

        <Box sx={{ borderRadius: '10px', overflow: 'hidden', mb: 1 }}>
          <AreaSearchMap
            center={mapCenter}
            boundary={confirmedBoundary}
            drawMode={drawing ? drawShape : null}
            onBoundaryDraftChange={setBoundaryDraft}
            resetToken={resetToken}
            gridCells={gridPreviewCells}
            height={320}
          />
        </Box>

        {confirmedBoundary ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5,
            bgcolor: '#ecfeff', border: '1px solid #a5f3fc', borderRadius: '8px', p: '10px 12px' }}>
            <Typography sx={{ fontSize: '13px', color: '#155e75' }}>
              {confirmedBoundary.type === 'circle'
                ? `Circle set — radius ${metersLabel(confirmedBoundary.radiusMeters)}`
                : `Freeform zone set — ${confirmedBoundary.points.length} points`}
            </Typography>
            <Button size="small" onClick={startOver} sx={{ color: '#0891b2', fontWeight: 600, whiteSpace: 'nowrap' }}>
              Redraw
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
            <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
              {boundaryDraft
                ? (boundaryDraft.type === 'circle'
                  ? `Radius: ${metersLabel(boundaryDraft.radiusMeters)}`
                  : `${boundaryDraft.points.length} point${boundaryDraft.points.length !== 1 ? 's' : ''}${boundaryDraft.closed ? ' — closed' : ''}`)
                : 'No shape drawn yet'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {boundaryDraft && <Button size="small" onClick={startOver} sx={{ color: '#64748b' }}>Cancel</Button>}
              <Button size="small" variant="contained" disabled={!boundaryDraft || !boundaryDraft.closed}
                onClick={confirmBoundary}
                sx={{ bgcolor: '#0891b2', fontWeight: 600, '&:hover': { bgcolor: '#0e7490' }, '&:disabled': { bgcolor: '#94a3b8' } }}>
                Confirm boundary
              </Button>
            </Box>
          </Box>
        )}

        {confirmedBoundary && (
          <Box sx={{ mt: 1, mb: 1 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
              Grid search mode (optional)
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#64748b', mb: 1 }}>
              For wilderness or off-road searches: overlay a grid of labeled cells (e.g. "B4") that
              color in as they're covered, instead of following streets.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: gridEnabled ? 1.5 : 0 }}>
              <Button size="small" variant={!gridEnabled ? 'contained' : 'outlined'} onClick={() => setGridEnabled(false)}
                sx={!gridEnabled
                  ? { bgcolor: '#64748b', '&:hover': { bgcolor: '#475569' } }
                  : { borderColor: '#cbd5e1', color: '#334155' }}>
                Off
              </Button>
              <Button size="small" variant={gridEnabled ? 'contained' : 'outlined'} onClick={() => setGridEnabled(true)}
                sx={gridEnabled
                  ? { bgcolor: '#0891b2', '&:hover': { bgcolor: '#0e7490' } }
                  : { borderColor: '#0891b2', color: '#0891b2' }}>
                On
              </Button>
            </Box>
            {gridEnabled && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <TextField size="small" type="number" label="Cell size (meters)" value={cellSizeMeters}
                  onChange={e => setCellSizeMeters(Math.max(5, Math.min(500, Number(e.target.value) || 10)))}
                  sx={{ width: 170 }} inputProps={{ min: 5, max: 500 }} />
                <Typography sx={{ fontSize: '12px', color: gridError ? '#dc2626' : '#64748b' }}>
                  {gridError || (gridPreview ? `${gridPreview.cells.length} cells (${gridPreview.rows}×${gridPreview.cols} grid)` : 'Computing…')}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        <Button
          variant="contained" fullWidth onClick={create}
          disabled={busy || !confirmedBoundary || (gridEnabled && (!gridPreview || !!gridError))}
          sx={{ p: '10px', mt: 2.5, bgcolor: '#0891b2', fontWeight: 600, borderRadius: '8px',
            '&:hover': { bgcolor: '#0e7490' }, '&:disabled': { bgcolor: '#94a3b8' } }}
        >
          {gridCreating || (busy ? 'Creating…' : 'Create search & get shareable link')}
        </Button>
      </Paper>
    </Container>
  );
}

// ─── Board view ─────────────────────────────────────────────────────────────

function AreaSearchBoard({ searchId }) {
  const [meta, setMeta] = useState(undefined); // undefined=loading, null=not found
  const [participants, setParticipants] = useState([]);
  const [trailPoints, setTrailPoints] = useState([]);
  const [pins, setPins] = useState([]);
  const [gridCellsRaw, setGridCellsRaw] = useState([]);
  const [identity, setIdentity] = useState(() => loadOrCreateParticipant(searchId));
  const [nameInput, setNameInput] = useState(() => identity.displayName || getLastDisplayName());
  const [addPinMode, setAddPinMode] = useState(false);
  const [pinDraft, setPinDraft] = useState(null);
  const [copied, setCopied] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [, forceTick] = useState(0);
  const [center, setCenter] = useState(null);
  const centerInit = useRef(false);
  const sharingRef = useRef(false);

  const searchRef = useMemo(() => doc(db, 'areaSearches', searchId), [searchId]);
  const participantsCol = useMemo(() => collection(db, 'areaSearches', searchId, 'participants'), [searchId]);
  const trailPointsCol = useMemo(() => collection(db, 'areaSearches', searchId, 'trailPoints'), [searchId]);
  const pinsCol = useMemo(() => collection(db, 'areaSearches', searchId, 'pins'), [searchId]);
  const gridCellsCol = useMemo(() => collection(db, 'areaSearches', searchId, 'gridCells'), [searchId]);
  const participantRef = useMemo(() => doc(participantsCol, identity.participantId), [participantsCol, identity.participantId]);

  const joined = Boolean(identity.displayName);

  // Live subscriptions.
  useEffect(() => onSnapshot(searchRef,
    snap => setMeta(snap.exists() ? snap.data() : null),
    () => setMeta(null)), [searchRef]);

  // Seed the map's center once from the stored creation-time center, not on
  // every snapshot — meta is a fresh object on every write (including the
  // lastActivityAt touch fired by each trail point), so deriving center
  // straight from meta.center would reset the map's zoom/pan on every GPS
  // ping while someone is sharing.
  useEffect(() => {
    if (centerInit.current || !meta) return;
    centerInit.current = true;
    if (meta.center) setCenter(meta.center);
  }, [meta]);

  useEffect(() => onSnapshot(query(participantsCol, orderBy('joinedAt', 'asc')),
    snap => setParticipants(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    () => {}), [participantsCol]);

  useEffect(() => onSnapshot(query(trailPointsCol, orderBy('createdAt', 'asc')),
    snap => setTrailPoints(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    () => {}), [trailPointsCol]);

  useEffect(() => onSnapshot(query(pinsCol, orderBy('createdAt', 'asc')),
    snap => setPins(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    () => {}), [pinsCol]);

  // Single-field orderBy (no composite index needed); secondary sort by col
  // happens client-side in the `gridCells` memo below.
  useEffect(() => onSnapshot(query(gridCellsCol, orderBy('row', 'asc')),
    snap => setGridCellsRaw(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    () => {}), [gridCellsCol]);

  // Relative "time ago" labels drift — tick every 20s to keep them current.
  useEffect(() => {
    const t = setInterval(() => forceTick(x => x + 1), 20000);
    return () => clearInterval(t);
  }, []);

  const touchActivity = () => updateDoc(searchRef, { lastActivityAt: serverTimestamp() }).catch(() => {});

  async function confirmName() {
    const trimmed = nameInput.trim();
    if (!trimmed) return;
    const wasJoined = joined;
    const updated = saveParticipantName(searchId, trimmed);
    setIdentity(updated);
    const payload = wasJoined
      ? { displayName: trimmed, lastSeenAt: serverTimestamp() }
      : { displayName: trimmed, color: updated.color, joinedAt: serverTimestamp(), sharing: false, lastSeenAt: serverTimestamp() };
    await setDoc(participantRef, payload, { merge: true }).catch(() => {});
  }

  // ── Location sharing ──
  // Grid search mode ties the throttle to the chosen cell size (smaller
  // cells need closer-spaced points to reliably register coverage) and
  // relaxes the accuracy cutoff (tree canopy commonly degrades GPS accuracy
  // readings well past the street-mode default, even for fixes still good
  // enough to place someone inside a cell).
  const gridTrackingOptions = useMemo(() => (
    meta && meta.grid && meta.grid.enabled
      ? { minDistanceM: Math.min(15, meta.grid.cellSizeMeters / 3), maxAccuracyM: 120 }
      : {}
  ), [meta]);
  const tracking = useLocationTracking(point => {
    addDoc(trailPointsCol, { participantId: identity.participantId, lat: point.lat, lng: point.lng, createdAt: serverTimestamp() }).catch(() => {});
    updateDoc(participantRef, { lastSeenAt: serverTimestamp() }).catch(() => {});
    touchActivity();
  }, gridTrackingOptions);
  sharingRef.current = tracking.sharing;

  function startSharing() {
    if (!joined) return;
    trackToolAction('/areasearch', 'Area Search Coordinator');
    tracking.start();
    updateDoc(participantRef, { sharing: true, lastSeenAt: serverTimestamp() }).catch(() => {});
  }
  function stopSharing() {
    tracking.stop();
    updateDoc(participantRef, { sharing: false }).catch(() => {});
  }

  // Best-effort: mark not-sharing if the tab is closed while active. Can't
  // reliably await a write here, so this is a safety net, not a guarantee.
  useEffect(() => {
    const onUnload = () => {
      if (sharingRef.current) updateDoc(participantRef, { sharing: false }).catch(() => {});
    };
    window.addEventListener('beforeunload', onUnload);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      onUnload();
    };
  }, [participantRef]);

  // ── Pins ──
  async function dropPinAt(lat, lng) {
    setAddPinMode(false);
    setPinDraft({ lat, lng, note: '' });
    const label = await reverseGeocode(lat, lng).catch(() => '');
    if (label) setPinDraft(d => (d && !d.id && !d.address ? { ...d, address: label } : d));
  }
  function useMyLocationForPin() {
    if (!navigator.geolocation) return alert('Location is not available in this browser.');
    navigator.geolocation.getCurrentPosition(
      pos => dropPinAt(pos.coords.latitude, pos.coords.longitude),
      () => alert('Could not get your location.'),
    );
  }
  async function savePin() {
    if (!pinDraft || pinDraft.lat == null) return;
    if (!joined) return alert('Enter your name above before dropping a pin.');
    const { id, note, lat, lng, address } = pinDraft;
    try {
      if (id) {
        await updateDoc(doc(pinsCol, id), { note: note.trim(), updatedAt: serverTimestamp() });
      } else {
        await addDoc(pinsCol, {
          lat, lng, note: note.trim(), address: address || '',
          participantId: identity.participantId, authorName: identity.displayName,
          createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
        });
      }
      touchActivity();
      setPinDraft(null);
    } catch (e) {
      alert(`Could not save pin: ${e.message}`);
    }
  }
  async function deletePin(id) {
    try { await deleteDoc(doc(pinsCol, id)); touchActivity(); setPinDraft(null); }
    catch (e) { alert(`Could not delete: ${e.message}`); }
  }
  function movePin(id, lat, lng) {
    updateDoc(doc(pinsCol, id), { lat, lng, updatedAt: serverTimestamp() }).catch(() => {});
  }
  function editPin(p) { setPinDraft({ ...p }); }

  // ── Grid search mode ──
  // Tests each trail point against every in-scope cell's stored bounds
  // rectangle (cheap 4-comparison test — cells are literal lat/lng rectangles
  // once created, no need to re-run point-in-shape). Runs redundantly in
  // every connected participant's browser since it's derived from the same
  // shared snapshot data; the idempotent map-key write means concurrent
  // redundant writes converge safely (same posture as touchActivity() etc.
  // elsewhere in this file).
  const triedHitsRef = useRef(new Set()); // "already attempted" trailPoint ids — pure optimization
  useEffect(() => {
    if (!meta || !meta.grid || !meta.grid.enabled || !gridCellsRaw.length || !trailPoints.length) return;
    for (const tp of trailPoints) {
      if (!tp.id || triedHitsRef.current.has(tp.id)) continue;
      for (const cell of gridCellsRaw) {
        if (cell.autoHitTrailPointIds && cell.autoHitTrailPointIds[tp.id]) continue;
        if (tp.lat < cell.boundsSouth || tp.lat > cell.boundsNorth || tp.lng < cell.boundsWest || tp.lng > cell.boundsEast) continue;
        updateDoc(doc(gridCellsCol, cell.id), {
          [`autoHitTrailPointIds.${tp.id}`]: true,
          ...(cell.autoCoveredAt ? {} : { autoCoveredAt: serverTimestamp(), autoCoveredBy: tp.participantId }),
        }).catch(() => {});
      }
      triedHitsRef.current.add(tp.id);
    }
  }, [trailPoints, gridCellsRaw, gridCellsCol, meta]);

  function toggleGridCellCoverage(cellId) {
    const cell = gridCellsRaw.find(c => c.id === cellId);
    if (!cell) return;
    const next = !cell.manualOverride;
    updateDoc(doc(gridCellsCol, cellId), {
      manualOverride: next,
      manualBy: next ? identity.participantId : null,
      manualAt: next ? serverTimestamp() : null,
    }).catch(() => {});
    touchActivity();
  }

  function copyLink() {
    navigator.clipboard.writeText(window.location.href)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); })
      .catch(() => {});
  }

  async function runExport() {
    setExporting(true);
    try {
      await exportAreaSearchPdf(meta, participants, trails, pdfPins, gridCells);
    } catch (e) {
      alert(`Could not export: ${e.message}`);
    } finally {
      setExporting(false);
    }
  }

  // ── Derived map data ──
  const colorByParticipant = useMemo(() => {
    const m = {};
    for (const p of participants) m[p.id] = p.color;
    return m;
  }, [participants]);

  // Single source of truth (computeCellTier) for a cell's color/status, so
  // the on-screen map and the PDF export can never disagree.
  const gridCells = useMemo(() => (
    gridCellsRaw
      .map(c => ({ ...c, tier: computeCellTier(c) }))
      .sort((a, b) => a.row - b.row || a.col - b.col)
  ), [gridCellsRaw]);

  const trails = useMemo(() => {
    const byId = {};
    for (const tp of trailPoints) {
      if (!byId[tp.participantId]) byId[tp.participantId] = [];
      byId[tp.participantId].push({ lat: tp.lat, lng: tp.lng });
    }
    return Object.entries(byId).map(([participantId, path]) => ({
      participantId, path, color: colorByParticipant[participantId] || '#334155',
    }));
  }, [trailPoints, colorByParticipant]);

  const mapPins = useMemo(() => pins.map(p => ({
    id: p.id, lat: p.lat, lng: p.lng,
    color: colorByParticipant[p.participantId] || '#0f172a',
    label: p.address || p.authorName || '',
  })), [pins, colorByParticipant]);

  const pdfPins = useMemo(() => pins.map(p => ({
    ...p, color: colorByParticipant[p.participantId] || '#0f172a',
  })), [pins, colorByParticipant]);

  if (meta === undefined) {
    return <Container maxWidth="md" sx={{ py: 6 }}><Typography sx={{ color: '#64748b' }}>Loading search…</Typography></Container>;
  }
  if (meta === null) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper elevation={0} sx={{ borderRadius: '14px', p: '28px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,.07)' }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 700, mb: 1 }}>Search not found</Typography>
          <Typography sx={{ color: '#64748b', fontSize: '14px', mb: 2 }}>
            This link may be mistyped, or the search was deleted.
          </Typography>
          <Button component={Link} to="/areasearch" variant="contained" sx={{ bgcolor: '#0891b2', '&:hover': { bgcolor: '#0e7490' } }}>
            Start a new search
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.4px' }}>{meta.title}</Typography>
          {meta.vehicleDesc && <Typography sx={{ color: '#64748b', mt: '4px', fontSize: '14px' }}>{meta.vehicleDesc}</Typography>}
          <Typography sx={{ color: '#94a3b8', mt: '4px', fontSize: '13px' }}>
            {participants.length} participant{participants.length !== 1 ? 's' : ''} · {pins.length} pin{pins.length !== 1 ? 's' : ''}
            {meta.grid && meta.grid.enabled && gridCells.length > 0
              ? ` · ${gridCells.filter(c => c.tier === 'confirmed').length}/${gridCells.length} grid cells confirmed`
              : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={copyLink} variant="contained"
            sx={{ bgcolor: copied ? '#16a34a' : '#0891b2', fontWeight: 600, borderRadius: '8px', whiteSpace: 'nowrap',
              '&:hover': { bgcolor: copied ? '#16a34a' : '#0e7490' } }}>
            {copied ? 'Link copied ✓' : 'Copy team link'}
          </Button>
          <Button onClick={runExport} variant="outlined" disabled={exporting}
            sx={{ borderColor: '#0891b2', color: '#0891b2', fontWeight: 600, borderRadius: '8px', whiteSpace: 'nowrap',
              '&:hover': { borderColor: '#0e7490', bgcolor: '#ecfeff' } }}>
            {exporting ? 'Exporting…' : 'Export PDF'}
          </Button>
        </Box>
      </Box>

      <SharedLocationBanner />

      {/* Join / name */}
      {!joined && (
        <Paper elevation={0} sx={{ borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,.07)', p: '16px', mb: 2, border: '1.5px solid #a5f3fc' }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', mb: 1 }}>Enter your name to join this search</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField fullWidth size="small" label="Your name" value={nameInput}
              onChange={e => setNameInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') confirmName(); }} />
            <Button variant="contained" onClick={confirmName} disabled={!nameInput.trim()}
              sx={{ bgcolor: '#0891b2', fontWeight: 600, whiteSpace: 'nowrap', '&:hover': { bgcolor: '#0e7490' } }}>
              Join
            </Button>
          </Box>
        </Paper>
      )}

      {/* Sharing toolbar */}
      <Paper elevation={0} sx={{ borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)', p: '16px', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {tracking.sharing ? (
            <Button onClick={stopSharing} variant="contained"
              sx={{ bgcolor: '#dc2626', fontWeight: 700, borderRadius: '8px', '&:hover': { bgcolor: '#b91c1c' } }}>
              ● Sharing location — Stop Sharing
            </Button>
          ) : (
            <Button onClick={startSharing} variant="contained" disabled={!joined}
              sx={{ bgcolor: '#0891b2', fontWeight: 700, borderRadius: '8px', '&:hover': { bgcolor: '#0e7490' }, '&:disabled': { bgcolor: '#94a3b8' } }}>
              Start Sharing My Location
            </Button>
          )}
          <Button onClick={() => { setAddPinMode(a => !a); setPinDraft(null); }} variant={addPinMode ? 'contained' : 'outlined'} disabled={!joined}
            sx={{ fontWeight: 600, borderRadius: '8px',
              ...(addPinMode ? { bgcolor: '#0891b2', '&:hover': { bgcolor: '#0e7490' } } : { borderColor: '#0891b2', color: '#0891b2' }) }}>
            {addPinMode ? 'Tap the map to drop a pin…' : '+ Drop a pin'}
          </Button>
          <Button onClick={useMyLocationForPin} variant="outlined" disabled={!joined}
            sx={{ borderColor: '#cbd5e1', color: '#334155', fontWeight: 600, borderRadius: '8px' }}>
            Pin my location
          </Button>
        </Box>
        {!joined && (
          <Typography sx={{ fontSize: '12px', color: '#94a3b8', mt: 1 }}>
            Enter your name above to start sharing your location or drop pins.
          </Typography>
        )}
        {tracking.error && (
          <Typography sx={{ fontSize: '12px', color: '#dc2626', mt: 1 }}>{tracking.error}</Typography>
        )}
        {tracking.sharing && (
          <Typography sx={{ fontSize: '12px', color: '#0e7490', mt: 1 }}>
            Your location is visible to anyone with this search's link until you stop sharing or close this tab.
          </Typography>
        )}
      </Paper>

      {/* Map */}
      <Paper elevation={0} sx={{ borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)', p: '12px', mb: 2 }}>
        <AreaSearchMap
          boundary={meta.boundary}
          trails={trails}
          pins={mapPins}
          gridCells={gridCells}
          youAreHere={tracking.here}
          center={center}
          addPinMode={addPinMode}
          onMapClick={dropPinAt}
          onPinClick={id => { const p = pins.find(x => x.id === id); if (p) editPin(p); }}
          onPinDragEnd={movePin}
          onGridCellClick={toggleGridCellCoverage}
        />
      </Paper>

      {/* Pin editor */}
      {pinDraft && (
        <Paper elevation={0} sx={{ borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,.07)', p: '20px', mb: 2, border: '1.5px solid #a5f3fc' }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', mb: 1.5 }}>
            {pinDraft.id ? 'Edit pin' : 'New pin'}
          </Typography>
          <TextField fullWidth size="small" label="Note" multiline minRows={2}
            placeholder="e.g. checked, nothing found — or: suspicious vehicle here"
            value={pinDraft.note || ''} onChange={e => setPinDraft(d => ({ ...d, note: e.target.value }))} />
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button onClick={savePin} variant="contained" sx={{ bgcolor: '#0891b2', fontWeight: 600, '&:hover': { bgcolor: '#0e7490' } }}>
              {pinDraft.id ? 'Save changes' : 'Save pin'}
            </Button>
            <Button onClick={() => setPinDraft(null)} variant="text" sx={{ color: '#64748b' }}>Cancel</Button>
            {pinDraft.id && <Button onClick={() => deletePin(pinDraft.id)} variant="text" sx={{ color: '#dc2626', ml: 'auto' }}>Delete</Button>}
          </Box>
        </Paper>
      )}

      {/* Pins */}
      <Paper elevation={0} sx={{ borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)', p: '16px', mb: 2 }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', mb: 1.5 }}>Pins</Typography>
        {pins.length === 0 ? (
          <Typography sx={{ color: '#94a3b8', fontSize: '13px' }}>No pins yet — drop one above to flag a spot.</Typography>
        ) : pins.map(p => {
          const color = colorByParticipant[p.participantId] || '#0f172a';
          return (
            <Box key={p.id} onClick={() => editPin(p)}
              sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', p: '10px 8px', borderTop: '1px solid #f1f5f9', cursor: 'pointer',
                '&:hover': { bgcolor: '#f8fafc' } }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color, mt: '3px', flexShrink: 0, border: '2px solid #fff', boxShadow: `0 0 0 1.5px ${color}` }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>
                  {p.address || `${(+p.lat).toFixed(5)}, ${(+p.lng).toFixed(5)}`}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#64748b', mt: '1px' }}>
                  {p.authorName || 'Unknown'}{p.createdAt ? ` · ${fmtAgo(p.createdAt)}` : ''}
                </Typography>
                {p.note && <Typography sx={{ fontSize: '12px', color: '#94a3b8', mt: '1px' }}>{p.note}</Typography>}
              </Box>
            </Box>
          );
        })}
      </Paper>

      {/* Roster */}
      <Paper elevation={0} sx={{ borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)', p: '16px' }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', mb: 1.5 }}>Participants</Typography>
        {participants.length === 0 ? (
          <Typography sx={{ color: '#94a3b8', fontSize: '13px' }}>No one has joined yet.</Typography>
        ) : participants.map(p => {
          const stale = !p.sharing || (Date.now() - (toDate(p.lastSeenAt)?.getTime() || 0)) > STALE_MS;
          return (
            <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: '8px 4px', borderTop: '1px solid #f1f5f9' }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: p.color, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#0f172a', flex: 1 }}>
                {p.displayName}{p.id === identity.participantId ? ' (you)' : ''}
              </Typography>
              <Typography sx={{ fontSize: '12px', color: stale ? '#94a3b8' : '#16a34a', fontWeight: 600 }}>
                {p.sharing && !stale ? 'Sharing now' : `Last seen ${fmtAgo(p.lastSeenAt) || 'a while ago'}`}
              </Typography>
            </Box>
          );
        })}
      </Paper>
    </Container>
  );
}

// ─── Route entry ────────────────────────────────────────────────────────────

export default function AreaSearch() {
  const { searchId } = useParams();
  return (
    <>
      <Navbar pageTitle="Area Search Coordinator" />
      {searchId ? <AreaSearchBoard searchId={searchId} /> : <CreateAreaSearch />}
    </>
  );
}
